'use client';

import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import Image from 'next/image';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { cn } from '../helpers/cn';
import { IconBack24 } from '../icons/ic-back';
import { IconClose24 } from '../icons/ic-close';
import { IconForth24 } from '../icons/ic-forth';

export type ImageGalleryProps = {
    images: string[];
    open: boolean;
    onClose: () => void;
    currentIndex?: number;
    /** Optional muted credit line shown under the top bar. */
    credits?: string;
};

// Horizontal swipe threshold (px) before a touch drag counts as a navigation gesture.
const SWIPE_THRESHOLD = 50;

const ArrowButton: React.FC<{
    direction: 'previous' | 'next';
    disabled?: boolean;
    onClick: () => void;
}> = ({ direction, disabled, onClick }) => (
    <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        aria-label={direction === 'previous' ? 'previous image' : 'next image'}
        className={cn(
            // Arrows are pointer-only chrome — hidden on touch (mobile uses swipe instead).
            'hidden lg:flex absolute top-1/2 -translate-y-1/2 z-20 items-center justify-center p-12 rounded-4 text-white transition-opacity',
            direction === 'previous' ? 'left-8 lg:left-16' : 'right-8 lg:right-16',
            disabled ? 'opacity-0 pointer-events-none' : 'opacity-80 hover:opacity-100 hover:bg-white/10 cursor-pointer'
        )}>
        {direction === 'previous' ? (
            <IconBack24 className="size-24 lg:size-32" />
        ) : (
            <IconForth24 className="size-24 lg:size-32" />
        )}
    </button>
);

/**
 * Modern fullscreen image gallery — mirrors the trailer reel modal's chrome: a black dimmed +
 * blurred backdrop, no surface card, a top bar (image counter + close), the current image centred
 * with a scale/opacity pop-in, prev/next arrows (desktop) + ←/→ keyboard + swipe (mobile), and a
 * bottom thumbnail strip to jump between images (active thumb auto-scrolled into view).
 */
function ImageGallery(props: ImageGalleryProps): React.JSX.Element {
    const { images, onClose } = props;

    const [currentImageIndex, setCurrentImageIndex] = useState(props.currentIndex ?? 0);
    const thumbnailStripRef = useRef<HTMLDivElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const touchStartX = useRef<number | null>(null);
    // Keep the latest onClose without re-running the history effect (which would push duplicate
    // entries) when the parent passes a new callback identity on each render.
    const onCloseRef = useRef(onClose);
    useEffect(() => {
        onCloseRef.current = onClose;
    });

    // Close on browser/hardware back (esp. mobile): push a history entry while the gallery is open,
    // then a back navigation pops it and closes the gallery instead of leaving the page.
    // StrictMode-safe: the push is deduped via the state marker and the cleanup ONLY removes the
    // listener — an earlier version called history.back() in the cleanup, which raced with the
    // StrictMode mount→unmount→mount cycle (back() is async) and closed the gallery a few
    // milliseconds after opening it.
    useEffect(() => {
        if (!props.open) return;
        if (!window.history.state?.cineamoGalleryOpen) {
            window.history.pushState({ cineamoGalleryOpen: true }, '');
        }
        const handlePopState = () => onCloseRef.current();
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [props.open]);

    // UI closes (X button, backdrop, ESC) route through the history entry, so popstate is the
    // single close path and the marker entry never lingers on the stack.
    const requestClose = useCallback(() => {
        if (window.history.state?.cineamoGalleryOpen) {
            window.history.back();
        } else {
            onCloseRef.current();
        }
    }, []);

    // Re-sync to the requested start index whenever the gallery (re)opens. The state write runs
    // inside rAF (after paint, never synchronously in the effect body) to satisfy the
    // react-hooks/set-state-in-effect rule.
    useEffect(() => {
        if (!props.open) return;
        const frame = requestAnimationFrame(() => setCurrentImageIndex(props.currentIndex ?? 0));
        return () => cancelAnimationFrame(frame);
    }, [props.open, props.currentIndex]);

    const showPreviousImage = useCallback(() => {
        setCurrentImageIndex((index) => Math.max(0, index - 1));
    }, []);

    const showNextImage = useCallback(() => {
        setCurrentImageIndex((index) => Math.min(images.length - 1, index + 1));
    }, [images.length]);

    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'ArrowLeft') {
                showPreviousImage();
            } else if (event.key === 'ArrowRight') {
                showNextImage();
            }
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [showPreviousImage, showNextImage]);

    // Keep the active thumbnail centred as the selection moves (click, swipe, arrows, keyboard).
    // Scrolls ONLY the strip container itself — scrollIntoView would also scroll/pan every
    // scrollable ancestor, which on mobile nudged the page/visual viewport a few px horizontally
    // (leaving the page slightly zoomed/pannable until a pinch-out reset).
    useEffect(() => {
        const strip = thumbnailStripRef.current;
        const active = strip?.querySelector<HTMLElement>('[data-active="true"]');
        if (!strip || !active) return;
        const stripRect = strip.getBoundingClientRect();
        const activeRect = active.getBoundingClientRect();
        const targetLeft =
            strip.scrollLeft + (activeRect.left - stripRect.left) - (strip.clientWidth - activeRect.width) / 2;
        strip.scrollTo({ left: targetLeft, behavior: 'smooth' });
    }, [currentImageIndex, props.open]);

    const onTouchStart = (event: React.TouchEvent) => {
        touchStartX.current = event.touches[0].clientX;
    };

    const onTouchEnd = (event: React.TouchEvent) => {
        if (touchStartX.current === null) return;
        const deltaX = event.changedTouches[0].clientX - touchStartX.current;
        if (deltaX <= -SWIPE_THRESHOLD) {
            showNextImage();
        } else if (deltaX >= SWIPE_THRESHOLD) {
            showPreviousImage();
        }
        touchStartX.current = null;
    };

    const currentImage = images[currentImageIndex];

    return (
        <Dialog
            open={props.open}
            onClose={requestClose}
            // initialFocus on the full-viewport panel: without it the focus trap focuses the first
            // focusable (the close button), and mobile browsers "reveal" focused elements by
            // panning/zooming the visual viewport a few px.
            initialFocus={panelRef}
            className="fixed z-modal-view inset-0 w-screen h-full">
            <DialogBackdrop
                transition
                className="fixed inset-0 bg-black/80 backdrop-blur-lg transition-opacity duration-300 data-[closed]:opacity-0"
            />

            {/* Panel fades as a whole; the image additionally pops in with a scale-up via the
            group-data-[closed] classes below. touch-manipulation kills the mobile double-tap-zoom
            heuristic for taps inside the gallery. */}
            <DialogPanel
                ref={panelRef}
                tabIndex={-1}
                transition
                className="group fixed inset-0 flex flex-col items-center select-none touch-manipulation transition-opacity duration-300 data-[closed]:opacity-0">
                {/* Top bar: image counter + close */}
                <div className="flex w-full items-center justify-between shrink-0 px-16 lg:px-48 py-16 lg:py-24">
                    <span className="text-body-bold-14 text-white/70">
                        {currentImageIndex + 1} / {images.length}
                    </span>
                    <button
                        type="button"
                        onClick={requestClose}
                        aria-label="close"
                        className="flex items-center justify-center p-8 rounded-4 text-white opacity-80 hover:opacity-100 hover:bg-white/10 transition-opacity cursor-pointer">
                        <IconClose24 className="size-24" />
                    </button>
                </div>

                {props.credits && <p className="shrink-0 px-16 lg:px-48 text-body-12 text-white/50">{props.credits}</p>}

                {/* Centred current image + prev/next arrows + mobile swipe */}
                <div
                    className="relative flex flex-1 min-h-px w-full items-center justify-center px-16 lg:px-72"
                    onTouchStart={onTouchStart}
                    onTouchEnd={onTouchEnd}>
                    <ArrowButton
                        direction="previous"
                        disabled={currentImageIndex <= 0}
                        onClick={showPreviousImage}
                    />
                    {currentImage && (
                        <Image
                            key={currentImageIndex}
                            src={currentImage}
                            alt={`Image ${currentImageIndex + 1}`}
                            width={1920}
                            height={1080}
                            sizes="100vw"
                            priority
                            className="max-h-full max-w-full w-auto h-auto object-contain rounded-8 transition duration-300 ease-out group-data-[closed]:scale-90 group-data-[closed]:opacity-0"
                        />
                    )}
                    <ArrowButton
                        direction="next"
                        disabled={currentImageIndex >= images.length - 1}
                        onClick={showNextImage}
                    />
                </div>

                {/* Bottom thumbnail strip — click a thumb to jump; the active one is highlighted. */}
                {images.length > 1 && (
                    // Scroll container stays left-aligned; the inner w-max track is centred via
                    // mx-auto ONLY when it fits — an overflowing track keeps its left edge reachable
                    // (justify-center on an overflowing flex scroller clips + hides the start).
                    <div
                        ref={thumbnailStripRef}
                        className="shrink-0 w-full overflow-x-auto no-scrollbar px-16 lg:px-48 py-16 lg:py-24">
                        <div className="flex gap-8 w-max mx-auto">
                            {images.map((image, index) => {
                                const isActive = index === currentImageIndex;
                                return (
                                    <button
                                        key={`${image}-${index}`}
                                        type="button"
                                        data-active={isActive}
                                        onClick={() => setCurrentImageIndex(index)}
                                        aria-label={`image ${index + 1}`}
                                        className={cn(
                                            // Fixed height, auto width — the image keeps its own aspect
                                            // ratio (h-full w-auto), so portrait posters and landscape
                                            // stills render in their true proportions.
                                            'flex-none h-48 lg:h-64 rounded-4 overflow-hidden transition-all duration-200',
                                            isActive
                                                ? 'ring-2 ring-cyan-middle opacity-100'
                                                : 'opacity-50 hover:opacity-100'
                                        )}>
                                        <Image
                                            src={image}
                                            alt={`Thumbnail ${index + 1}`}
                                            width={320}
                                            height={180}
                                            sizes="160px"
                                            className="h-full w-auto object-cover"
                                        />
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}
            </DialogPanel>
        </Dialog>
    );
}

export default ImageGallery;
